import { PushNotifications } from '@capacitor/push-notifications'
import type { Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications'
import { platform } from './platform'
import { API_URL } from '../lib/supabase'
import { playForNotificationType } from '../lib/sound'

/**
 * Android notification channels. Android 8+ ignores a payload's sound/importance
 * unless the channel that carries it was created with them — so we declare them
 * once here and the backend targets them by `channelId`. Distinct channels also
 * let the user tune each category (sound/vibration) in system settings.
 */
const CHANNELS = [
  { id: 'cycle_missed', name: 'Missed cleaning cycles', description: 'A scheduled cycle was not logged in time', importance: 5 as const, sound: 'default', vibration: true },
  { id: 'alerts', name: 'Alerts', description: 'Supervisor alerts and instructions', importance: 5 as const, sound: 'default', vibration: true },
  { id: 'general', name: 'General', description: 'General app notifications', importance: 4 as const, sound: 'default', vibration: true }
]

async function ensureChannels (): Promise<void> {
  if (!platform.isAndroid()) return
  try {
    for (const ch of CHANNELS) {
      await PushNotifications.createChannel({
        id: ch.id,
        name: ch.name,
        description: ch.description,
        importance: ch.importance,   // 5 = HIGH (heads-up + sound), 4 = DEFAULT
        sound: ch.sound,             // 'default' → system notification sound
        vibration: ch.vibration,
        visibility: 1,
        lights: true
      })
    }
    console.log('[NativePush] notification channels ready')
  } catch (err) {
    console.warn('[NativePush] could not create channels:', err)
  }
}

export interface NativePushSubscription {
  token: string
  platform: 'ios' | 'android'
}

// Listeners for push events
type PushEventCallback = (notification: PushNotificationSchema) => void
type TokenCallback = (token: string) => void

let tokenListeners: TokenCallback[] = []
let notificationListeners: PushEventCallback[] = []
let actionListeners: ((action: ActionPerformed) => void)[] = []

let soundInitDone = false

// Native push notifications service (FCM/APNs)
export const nativePushNotifications = {
  // Check if native push is supported
  isSupported(): boolean {
    return platform.isNative()
  },

  /** Maps Capacitor's permission state to the shared notification vocabulary. */
  async getPermission(): Promise<'default' | 'granted' | 'denied'> {
    if (!platform.isNative()) return 'denied'
    const status = await PushNotifications.checkPermissions()
    if (status.receive === 'granted') return 'granted'
    if (status.receive === 'denied') return 'denied'
    return 'default'
  },

  /**
   * Lightweight startup setup, safe to call before login: declares the Android
   * notification channels (so background pushes make sound) and registers the
   * foreground handler (so an in-app push is audible too). Does NOT prompt for
   * permission or register a token — that stays with the full `init()` flow
   * behind the user's Settings opt-in.
   */
  async initSound(): Promise<void> {
    if (!platform.isNative() || soundInitDone) return
    soundInitDone = true
    await ensureChannels()
    try {
      await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
        const type = (notification.data?.type as string | undefined) ?? notification.data?.channelId
        playForNotificationType(type)
        notificationListeners.forEach(cb => cb(notification))
      })
    } catch (err) {
      console.warn('[NativePush] initSound listener failed:', err)
    }
  },

  // Initialize push notifications
  async init(): Promise<void> {
    if (!platform.isNative()) return

    // Request permissions
    const permStatus = await PushNotifications.checkPermissions()

    if (permStatus.receive === 'prompt') {
      await PushNotifications.requestPermissions()
    }

    // Declare channels + register the foreground sound listener (idempotent).
    await this.initSound()

    // Register listeners
    PushNotifications.addListener('registration', (token: Token) => {
      if (token?.value) {
        console.log('[NativePush] ✅ Registration success, token:', token.value.substring(0, 20) + '...')
        tokenListeners.forEach(cb => cb(token.value))
      } else {
        console.error('[NativePush] ❌ Registration received invalid token:', token)
      }
    })

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('[NativePush] ❌ Registration error event:', {
        message: error?.message || 'Unknown error',
        error: error
      })
    })

    // pushNotificationReceived is registered by initSound() above (with the
    // foreground sound cue), so it's not re-added here.

    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      console.log('Push action performed:', action)
      actionListeners.forEach(cb => cb(action))
    })
  },

  // Register for push notifications
  async register(): Promise<string | null> {
    if (!platform.isNative()) {
      console.warn('[NativePush] Not a native platform')
      return null
    }

    try {
      // Ensure init() is called first to set up listeners
      await this.init()
      
      console.log('[NativePush] Checking permissions...')
      const permStatus = await PushNotifications.checkPermissions()
      console.log('[NativePush] Permission status:', JSON.stringify(permStatus))
      
      if (permStatus.receive !== 'granted') {
        console.log('[NativePush] Requesting permissions...')
        const requestResult = await PushNotifications.requestPermissions()
        console.log('[NativePush] Permission request result:', JSON.stringify(requestResult))
        
        if (requestResult.receive !== 'granted') {
          console.warn('[NativePush] ❌ Push notification permission denied')
          return null
        }
      }

      console.log('[NativePush] Registering with FCM/APNs...')
      
      // Set up token listener BEFORE calling register()
      const tokenPromise = new Promise<string | null>((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('[NativePush] ⏱️ Token registration timeout after 15 seconds')
          tokenListeners = tokenListeners.filter(cb => cb !== listener)
          resolve(null)
        }, 15000) // Increased timeout to 15 seconds
        
        const listener = (token: string) => {
          console.log('[NativePush] ✅ Token received:', token.substring(0, 20) + '...')
          clearTimeout(timeout)
          tokenListeners = tokenListeners.filter(cb => cb !== listener)
          resolve(token)
        }
        
        tokenListeners.push(listener)
      })
      
      // Register with FCM/APNs
      await PushNotifications.register()
      console.log('[NativePush] Registration call completed, waiting for token...')

      // Wait for token
      return await tokenPromise
    } catch (error: any) {
      console.error('[NativePush] ❌ Registration error:', {
        message: error?.message || 'Unknown error',
        error: error,
        stack: error?.stack
      })
      return null
    }
  },

  // Subscribe to push and register token with backend
  async subscribe(userId?: string): Promise<NativePushSubscription | null> {
    if (!platform.isNative()) {
      console.warn('[NativePush] Not a native platform')
      return null
    }
    
    if (!userId) {
      console.error('[NativePush] Cannot subscribe: userId is required')
      return null
    }

    try {
      console.log('[NativePush] Starting subscription process for user:', userId)
      
      const token = await this.register()
      if (!token) {
        console.error('[NativePush] Failed to get push token from device')
        return null
      }

      console.log('[NativePush] Got token, registering with backend...')
      const subscription: NativePushSubscription = {
        token,
        platform: platform.isIOS() ? 'ios' : 'android'
      }

      // Get auth token for the request
      const { supabase } = await import('../lib/supabase')
      const session = await supabase.auth.getSession()
      const authToken = session.data.session?.access_token

      // Register with backend
      console.log('[NativePush] Making API request to:', `${API_URL}/api/device-tokens`)
      console.log('[NativePush] Request payload:', {
        user_id: userId,
        token: subscription.token.substring(0, 20) + '...',
        platform: subscription.platform,
        hasAuthToken: !!authToken
      })
      
      let response: Response
      try {
        response = await fetch(`${API_URL}/api/device-tokens`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
          },
          body: JSON.stringify({
            user_id: userId,
            token: subscription.token,
            platform: subscription.platform
          })
        })
      } catch (fetchError: any) {
        console.error('[NativePush] ❌ Network error during fetch:', fetchError?.message || fetchError)
        console.error('[NativePush] ❌ Full fetch error:', JSON.stringify({
          message: fetchError?.message,
          name: fetchError?.name,
          stack: fetchError?.stack
        }, null, 2))
        throw fetchError
      }

      console.log('[NativePush] Response status:', response.status, response.statusText)

      if (!response.ok) {
        let errorText = ''
        try {
          errorText = await response.text()
        } catch (e: any) {
          errorText = `Failed to read error response: ${e?.message || String(e)}`
        }
        
        const errorDetails = {
          status: response.status,
          statusText: response.statusText,
          url: `${API_URL}/api/device-tokens`,
          error: errorText,
          body: {
            user_id: userId,
            token: subscription.token.substring(0, 20) + '...',
            platform: subscription.platform
          }
        }
        
        console.error('[NativePush] ❌ Failed to register token with server:')
        console.error(JSON.stringify(errorDetails, null, 2))
        throw new Error(`Failed to register token: ${response.status} - ${errorText}`)
      }

      const result = await response.json()
      console.log('[NativePush] ✅ Device token registered successfully:', result)
      
      return subscription
    } catch (error: any) {
      const errorDetails = {
        message: error?.message || 'Unknown error',
        name: error?.name,
        stack: error?.stack,
        response: error?.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        } : undefined
      }
      console.error('[NativePush] ❌ Subscribe error:', JSON.stringify(errorDetails, null, 2))
      console.error('[NativePush] ❌ Full error object:', error)
      return null
    }
  },

  // Unsubscribe from push notifications
  async unsubscribe(userId?: string, token?: string): Promise<boolean> {
    if (!platform.isNative()) {
      console.warn('[NativePush] Not a native platform')
      return false
    }

    if (!userId) {
      console.error('[NativePush] Cannot unsubscribe: userId is required')
      return false
    }

    try {
      console.log('[NativePush] Starting unsubscribe process for user:', userId)
      
      // Get auth token
      const { supabase } = await import('../lib/supabase')
      const session = await supabase.auth.getSession()
      const authToken = session.data.session?.access_token

      // If token not provided, try to get it from backend
      let tokenToDelete = token
      if (!tokenToDelete) {
        console.log('[NativePush] Token not provided, fetching from backend...')
        try {
          const response = await fetch(`${API_URL}/api/device-tokens/${userId}`, {
            headers: authToken ? {
              'Authorization': `Bearer ${authToken}`
            } : {}
          })
          
          if (response.ok) {
            const data = await response.json()
            if (data.success && data.data && data.data.length > 0) {
              // Delete all tokens for this user
              tokenToDelete = data.data[0].token
              if (tokenToDelete) {
                console.log('[NativePush] Found token to delete:', tokenToDelete.substring(0, 20) + '...')
              }
            }
          }
        } catch (error) {
          console.warn('[NativePush] Could not fetch token from backend:', error)
        }
      }

      // Remove from backend
      if (tokenToDelete) {
        console.log('[NativePush] Deleting token from backend...')
        const response = await fetch(`${API_URL}/api/device-tokens`, {
          method: 'DELETE',
          headers: { 
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
          },
          body: JSON.stringify({ user_id: userId, token: tokenToDelete as string })
        })

        if (!response.ok) {
          const errorText = await response.text()
          console.error('[NativePush] Failed to delete token from server:', {
            status: response.status,
            error: errorText
          })
        } else {
          console.log('[NativePush] ✅ Token deleted from backend')
        }
      } else {
        console.warn('[NativePush] No token to delete')
      }

      // Unregister from FCM/APNs (note: this doesn't fully unregister, just removes listeners)
      await PushNotifications.removeAllListeners()
      console.log('[NativePush] ✅ Unsubscribed successfully')
      
      return true
    } catch (error: any) {
      console.error('[NativePush] ❌ Unsubscribe error:', {
        message: error?.message || 'Unknown error',
        error: error
      })
      return false
    }
  },

  // Add listener for received notifications
  onNotificationReceived(callback: PushEventCallback): () => void {
    notificationListeners.push(callback)
    return () => {
      notificationListeners = notificationListeners.filter(cb => cb !== callback)
    }
  },

  // Add listener for notification actions (tapped)
  onNotificationAction(callback: (action: ActionPerformed) => void): () => void {
    actionListeners.push(callback)
    return () => {
      actionListeners = actionListeners.filter(cb => cb !== callback)
    }
  },

  // Get pending notifications (for when app opens)
  async getDeliveredNotifications(): Promise<PushNotificationSchema[]> {
    if (!platform.isNative()) return []
    
    try {
      const result = await PushNotifications.getDeliveredNotifications()
      return result.notifications
    } catch {
      return []
    }
  },

  // Clear all delivered notifications
  async clearNotifications(): Promise<void> {
    if (!platform.isNative()) return
    await PushNotifications.removeAllDeliveredNotifications()
  }
}
